import {
  LayoutDashboard,
  Building2,
  Users,
  TrendingUp,
  CreditCard,
  DollarSign,
  Settings2,
  LogOut,
  ShieldCheck,
  BarChart3,
  Footprints,
  Brain,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

const adminNav = [
  { title: "Overview", url: "/", icon: LayoutDashboard },
  { title: "Businesses", url: "/admin/businesses", icon: Building2 },
  { title: "Users", url: "/admin/users", icon: Users },
  { title: "Insights", url: "/admin/alerts", icon: TrendingUp },
  { title: "Subscriptions", url: "/admin/subscriptions", icon: CreditCard },
  { title: "Finance", url: "/admin/finance", icon: DollarSign },
  { title: "Usage", url: "/admin/usage", icon: BarChart3 },
  { title: "Activation", url: "/admin/activation", icon: Footprints },
  { title: "Behavior", url: "/admin/behavior", icon: Brain },
  { title: "System", url: "/admin/system", icon: Settings2 },
];

export function AppSidebar() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    await signOut();
    toast({ title: "Signed out" });
    navigate("/auth", { replace: true });
  };

  return (
    <Sidebar className="border-r-0">
      <div className="flex h-14 items-center gap-3 px-5 border-b border-sidebar-border">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary">
          <ShieldCheck className="h-4 w-4 text-sidebar-primary-foreground" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-display font-bold text-sidebar-accent-foreground">Easy Rental</span>
          <span className="text-[10px] text-sidebar-foreground/60 uppercase tracking-wider">Admin</span>
        </div>
      </div>

      <SidebarContent className="px-2 pt-4">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 text-[10px] uppercase tracking-widest mb-1">
            Platform
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/"}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        {user && (
          <div className="px-3 py-2 mb-1">
            <p className="text-xs font-medium text-sidebar-accent-foreground truncate">{user.email}</p>
            <p className="text-[10px] text-sidebar-foreground/50">Platform Admin</p>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleSignOut}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors cursor-pointer"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
